import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { authService } from '../services';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state from storage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        if (authService.isAuthenticated()) {
          const storedAdmin = authService.getStoredAdmin();
          if (storedAdmin) {
            setAdmin(storedAdmin);
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        authService.logout();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login admin
   */
  const login = useCallback(async (email, password, rememberMe = true) => {
    try {
      setLoading(true);
      const result = await authService.login(email, password, rememberMe);
      
      if (result.success) {
        setAdmin(result.data);
        setIsAuthenticated(true);
        return { success: true, data: result.data };
      }
      
      return { success: false, error: result.error };
    } catch (error) {
      return { success: false, error: error.message || 'حدث خطأ أثناء تسجيل الدخول' };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout admin
   */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      setAdmin(null);
      setIsAuthenticated(false);
    }
  }, []);

  /**
   * Logout from all devices
   */
  const logoutAll = useCallback(async () => {
    try {
      await authService.logoutAll();
    } finally {
      setAdmin(null);
      setIsAuthenticated(false);
    }
  }, []);

  /**
   * Update admin data
   */
  const updateAdmin = useCallback((updates) => {
    const updated = authService.updateStoredAdmin(updates);
    if (updated) {
      setAdmin(updated);
    }
  }, []);

  // Memoized context value
  const value = useMemo(() => ({
    admin,
    loading,
    isAuthenticated,
    login,
    logout,
    logoutAll,
    updateAdmin
  }), [admin, loading, isAuthenticated, login, logout, logoutAll, updateAdmin]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
